import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Inbox, Send, Mail, MailOpen, ArrowLeft, Loader2, 
  MessageSquare, User, Building2, Clock, Reply
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [tab, setTab] = useState("inbox"); // inbox, sent
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  // Reply/New message
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyForm, setReplyForm] = useState({ subject: "", content: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchMessages();
  }, [user, tab]);

  useEffect(() => {
    // Se arriva con query param per nuovo messaggio
    const recipientId = searchParams.get("to");
    const recipientName = searchParams.get("name");
    if (recipientId && recipientName) {
      setReplyTo({ id: recipientId, name: decodeURIComponent(recipientName) });
      setReplyForm({ subject: "", content: "" });
      setShowReplyModal(true);
    }
  }, [searchParams]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const endpoint = tab === "inbox" ? "/messages/inbox" : "/messages/sent";
      const res = await axios.get(`${API}${endpoint}`);
      setMessages(res.data);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMessage = async (msg) => {
    setSelectedMessage(msg);
    if (tab === "inbox" && !msg.is_read) {
      try {
        await axios.put(`${API}/messages/${msg.id}/read`);
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
      } catch (error) {
        console.error("Failed to mark message as read:", error);
      }
    }
  };

  const handleReply = (msg) => {
    setReplyTo({ id: msg.sender_id, name: msg.sender_name });
    setReplyForm({ 
      subject: `Re: ${msg.subject}`, 
      content: "" 
    });
    setSelectedMessage(null);
    setShowReplyModal(true);
  };

  const handleSendMessage = async () => {
    if (!replyTo || !replyForm.subject.trim() || !replyForm.content.trim()) {
      toast.error("Compila tutti i campi");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: replyTo.id,
        subject: replyForm.subject,
        content: replyForm.content
      });
      toast.success("Messaggio inviato!");
      setShowReplyModal(false);
      setReplyTo(null);
      setReplyForm({ subject: "", content: "" });
      if (tab === "sent") fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString("it-CH", { hour: "2-digit", minute: "2-digit" });
    } else if (diffDays === 1) {
      return "Ieri";
    } else if (diffDays < 7) {
      return date.toLocaleDateString("it-CH", { weekday: "short" });
    } else {
      return date.toLocaleDateString("it-CH", { day: "numeric", month: "short" });
    }
  };

  const unreadCount = messages.filter(m => !m.is_read && tab === "inbox").length;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="messages-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-[#004E64]" />
              Messaggi
            </h1>
            <p className="text-slate-500 text-sm mt-1">Gestisci le tue conversazioni</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={tab === "inbox" ? "default" : "outline"}
            onClick={() => setTab("inbox")}
            className={tab === "inbox" ? "bg-[#004E64] hover:bg-[#003B4D]" : ""}
          >
            <Inbox className="w-4 h-4 mr-2" />
            Ricevuti
            {unreadCount > 0 && (
              <Badge className="ml-2 bg-[#DA291C]">{unreadCount}</Badge>
            )}
          </Button>
          <Button
            variant={tab === "sent" ? "default" : "outline"}
            onClick={() => setTab("sent")}
            className={tab === "sent" ? "bg-[#004E64] hover:bg-[#003B4D]" : ""}
          >
            <Send className="w-4 h-4 mr-2" />
            Inviati
          </Button>
        </div>

        {/* Messages List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : messages.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    onClick={() => handleOpenMessage(msg)}
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      !msg.is_read && tab === "inbox" ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        !msg.is_read && tab === "inbox" ? "bg-[#004E64]" : "bg-slate-200"
                      }`}>
                        {!msg.is_read && tab === "inbox" ? (
                          <Mail className="w-5 h-5 text-white" />
                        ) : (
                          <MailOpen className="w-5 h-5 text-slate-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`font-medium truncate ${
                            !msg.is_read && tab === "inbox" ? "text-slate-900" : "text-slate-700"
                          }`}>
                            {tab === "inbox" ? msg.sender_name : msg.recipient_name}
                          </span>
                          <span className="text-xs text-slate-400 whitespace-nowrap">
                            {formatDate(msg.created_at)}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${
                          !msg.is_read && tab === "inbox" ? "font-medium text-slate-800" : "text-slate-600"
                        }`}>
                          {msg.subject}
                        </p>
                        <p className="text-xs text-slate-400 truncate mt-0.5">
                          {msg.content.substring(0, 80)}...
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">
                  {tab === "inbox" ? "Nessun messaggio ricevuto" : "Nessun messaggio inviato"}
                </h3>
                <p className="text-slate-500 text-sm">
                  {tab === "inbox" 
                    ? "I messaggi che riceverai appariranno qui" 
                    : "I messaggi che invierai appariranno qui"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Message Detail Modal */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-lg">
          {selectedMessage && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-8">{selectedMessage.subject}</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-3 pb-4 border-b">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">
                      {tab === "inbox" ? selectedMessage.sender_name : selectedMessage.recipient_name}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(selectedMessage.created_at).toLocaleDateString("it-CH", {
                        day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit"
                      })}
                    </p>
                  </div>
                </div>

                <div className="prose prose-sm max-w-none">
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedMessage.content}</p>
                </div>
              </div>

              {tab === "inbox" && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSelectedMessage(null)}>
                    Chiudi
                  </Button>
                  <Button 
                    onClick={() => handleReply(selectedMessage)}
                    className="bg-[#004E64] hover:bg-[#003B4D]"
                  >
                    <Reply className="w-4 h-4 mr-2" /> Rispondi
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply/New Message Modal */}
      <Dialog open={showReplyModal} onOpenChange={(open) => {
        setShowReplyModal(open);
        if (!open) {
          setReplyTo(null);
          setReplyForm({ subject: "", content: "" });
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {replyForm.subject.startsWith("Re:") ? "Rispondi" : "Nuovo messaggio"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-500">A:</span>
              <span className="font-medium text-slate-900">{replyTo?.name}</span>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Oggetto
              </label>
              <Input
                value={replyForm.subject}
                onChange={(e) => setReplyForm({ ...replyForm, subject: e.target.value })}
                placeholder="Oggetto del messaggio"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Messaggio
              </label>
              <Textarea
                value={replyForm.content}
                onChange={(e) => setReplyForm({ ...replyForm, content: e.target.value })}
                placeholder="Scrivi il tuo messaggio..."
                rows={6}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplyModal(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSendMessage}
              disabled={sending}
              className="bg-[#DA291C] hover:bg-[#B91C1C]"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Invia</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
